// Kontoret sender selv til kunden (bookingskuffen og kundekortet) – samme kode i testversionen og i drift:
//  • en valgt skabelon: bekræftelsen eller påmindelsen (virksomhedens egne automatiseringer, flettet som når de sendes af sig selv),
//    "Se og ret din booking" (login-link) eller linket til at vælge en ny tid (mangler tid),
//  • eller en fri SMS/mail. Mails får kundens engangslogin (pladsholder – linket laves først af send-mail), når de handler om en booking.
//  • Afsenderen er bookingens brand (afsender.ts); forhåndsvisningen viser præcis det, kunden får, inkl. "Powered by".
//  • Grænser (rate limit): højst BESKED_GRAENSE.modtager beskeder til samme kunde og BESKED_GRAENSE.klient i alt fra kontoret pr.
//    BESKED_GRAENSE.minutter minutter – også for "Send igen". Spærret udsendelse: intet sendes (vises tydeligt).
import { STANDARD_WORKFLOWS, opfyldt, udfyld, variabler, type Kontekst, type Workflow } from './automation.ts';
import { byggBesked } from './automationMotor.ts';
import { KUNDELINK_SKABELON, LINK_PLADSHOLDER, kundeKontoMail, medLoginLink } from './kundekonto.ts';
import { MT_SKABELON, kundeBesked as nyTidBesked } from './manglerTid.ts';
import { medPoweredBy } from './afsender.ts';
import { datoTekst, tidsrum } from './dato.ts';
import { rens, rensTekst } from './validering.ts';
import { smsDele } from '../../supabase/functions/_shared/sms.ts';
import { ApiFejl } from './fejl.ts';

export type KundeSkabelon = 'bekraeftelse' | 'paamindelse' | 'login' | 'mangler_tid';
export const KUNDE_SKABELONER: [KundeSkabelon, string][] = [
  ['bekraeftelse', 'Bekræftelse'], ['paamindelse', 'Påmindelse'], ['login', 'Se og ret din booking (login-link)'], ['mangler_tid', 'Vælg ny tid (mangler tid)'],
];
export type Kanal = 'sms' | 'mail';
export type KundeBeskedInput = {
  booking_id?: number | null; kunde_id?: string | null; kanal: Kanal | 'begge';
  skabelon?: KundeSkabelon | null;               // null/udeladt: fri besked (emne, tekst, smsTekst)
  emne?: string; tekst?: string; smsTekst?: string;
};
/** Én besked, klar til at blive lagt i kø. vis: det kunden ser (med "Powered by"); tekst: det, der gemmes (send-mail/-besked sætter "Powered by" på). */
export type BeskedUdkast = { kanal: Kanal; til: string; fra: string; emne: string; tekst: string; vis: string; skabelon: string; kundelogin: boolean; link: string | null; ics?: string; dele: number };

export const BESKED_GRAENSE = { modtager: 5, klient: 30, minutter: 10 } as const;
export const SPAERRET = 'Udsendelse er spærret – intet bliver sendt.';
export const FOR_MANGE = `For mange beskeder på kort tid. Vent lidt, og prøv igen (højst ${BESKED_GRAENSE.modtager} til samme kunde pr. ${BESKED_GRAENSE.minutter} min.).`;

/** Tjek grænserne ud fra tidspunkterne (ms) for kontorets seneste beskeder – til modtageren og i alt. */
export function tjekGraense(tilModtager: number[], iAlt: number[], nu: number): void {
  const fra = nu - BESKED_GRAENSE.minutter * 60_000;
  if (tilModtager.filter((t) => t >= fra).length >= BESKED_GRAENSE.modtager || iAlt.filter((t) => t >= fra).length >= BESKED_GRAENSE.klient) throw new ApiFejl(FOR_MANGE);
}

export type Afsendere = { mail: string; sms: string; powered: string | null };
export type UdkastData = {
  kunde: { navn: string; email: string; telefon: string };
  k: Kontekst | null;                   // bookingen (null: kun kunden – så kun fri besked)
  workflows: Workflow[] | null;          // virksomhedens automatiseringer (null: standarden)
  login: boolean;                        // kunden kan få login-knappen (kundelogin slået til, e-mail, ikke en medarbejder)
  nyTid: string | null;                  // linket til at vælge ny tid, når bookingen mangler en tid
  firma: string; telefon: string;        // brandets navn og telefon (uden booking: virksomhedens)
  afsender: Afsendere; klient: string; nu: number;
};

const kanaler = (k: KundeBeskedInput['kanal']): Kanal[] => (k === 'begge' ? ['sms', 'mail'] : [k]);

/** Skabelonen fra automatiseringerne: den første aktive regel for hændelsen, hvis betingelser passer, med en handling til kunden på kanalen. */
function fraRegel(d: UdkastData, k: Kontekst, kanal: Kanal, valg: (w: Workflow) => boolean): { w: Workflow; h: Workflow['handlinger'][number] } | null {
  const l = [...(d.workflows?.length ? d.workflows : STANDARD_WORKFLOWS)].sort((a, b) => a.sortering - b.sortering);
  for (const w of l) {
    if (!w.aktiv || !valg(w) || !opfyldt(w, k)) continue;
    const h = w.handlinger.find((x) => x.til === 'kunde' && x.type === kanal);
    if (h) return { w, h };
  }
  return null;
}

/** Beskederne, kontoret er ved at sende (forhåndsvisningen og selve afsendelsen bruger det samme). */
export function kundeUdkast(a: KundeBeskedInput, d: UdkastData): BeskedUdkast[] {
  if (!['sms', 'mail', 'begge'].includes(a.kanal)) throw new ApiFejl('Vælg SMS eller mail.');
  const k = d.k;
  if (a.skabelon && !k) throw new ApiFejl('Vælg en booking for at sende en skabelon.');
  const ud: BeskedUdkast[] = [];
  for (const kanal of kanaler(a.kanal)) {
    const til = kanal === 'mail' ? d.kunde.email : d.kunde.telefon;
    if (!til) throw new ApiFejl(kanal === 'mail' ? 'Kunden har ingen e-mail.' : 'Kunden har intet mobilnummer.');
    const login = kanal === 'mail' && d.login && !!k;
    let emne = '', tekst = '', skabelon = 'Direkte besked', link: string | null = null, ics: string | undefined;
    if (a.skabelon && k) {
      const navn = KUNDE_SKABELONER.find(([id]) => id === a.skabelon)?.[1];
      if (!navn) throw new ApiFejl('Ukendt skabelon.');
      if (a.skabelon === 'bekraeftelse' || a.skabelon === 'paamindelse') {
        const r = a.skabelon === 'bekraeftelse'
          ? fraRegel(d, k, kanal, (w) => w.haendelse === (k.status === 'afventer' ? 'booking_oprettet' : 'booking_godkendt'))
          : fraRegel(d, { ...k, status: k.status === 'afventer' ? 'godkendt' : k.status }, kanal, (w) => w.haendelse === 'booking_start' && w.forskydning?.retning === 'foer');
        if (!r) throw new ApiFejl(`Der er ingen ${navn.toLowerCase()} på ${kanal === 'mail' ? 'mail' : 'SMS'} til denne booking. Skriv en fri besked i stedet.`);
        const b = byggBesked(r.w, r.h, k, { login, steamer_email: '', firma_email: '', klient: d.klient, nu: d.nu });
        emne = b.emne; tekst = b.tekst; skabelon = r.w.navn; ics = b.ics;
      } else if (a.skabelon === 'login') {
        if (kanal === 'mail') {
          const m = kundeKontoMail({ ny: false, navn: d.kunde.navn, firma: d.firma, link: login ? LINK_PLADSHOLDER : k.links.mine,
            booking: { nr: k.booking_id, ydelse: k.ydelse, dato: k.dato ? datoTekst(k.dato) : 'dato aftales', tid: k.start != null ? tidsrum(k.start, k.slut) : 'tid aftales', adresse: `${k.adresse}, ${k.postnr} ${k.by}` } });
          emne = m.emne; tekst = m.tekst;
        } else tekst = `Hej ${fornavn(d.kunde.navn)}. Se og ret din booking hos ${d.firma} her: ${k.links.mine} Mvh ${d.firma}`;
        skabelon = KUNDELINK_SKABELON;
      } else {
        if (!d.nyTid) throw new ApiFejl('Bookingen mangler ikke en tid – der er intet link til en ny tid.');
        const m = nyTidBesked(kanal, 1, { fornavn: fornavn(d.kunde.navn), firma: d.firma, telefon: d.telefon, link: d.nyTid });
        emne = m.emne; tekst = login ? medLoginLink(m.tekst, LINK_PLADSHOLDER) : m.tekst; skabelon = MT_SKABELON; link = d.nyTid;
      }
    } else {
      const raa = kanal === 'mail' ? rensTekst(a.tekst ?? '', 5000) : rensTekst(a.smsTekst ?? a.tekst ?? '', 1000);
      if (raa.trim().length < 2) throw new ApiFejl('Skriv en besked.', { [kanal === 'mail' ? 'tekst' : 'sms']: 'Skriv en besked.' });
      if (kanal === 'mail' && rens(a.emne ?? '', 200).length < 2) throw new ApiFejl('Skriv et emne.', { emne: 'Skriv et emne.' });
      const v = k ? variabler(login ? { ...k, links: { ...k.links, mine: LINK_PLADSHOLDER } } : k)
        : { kunde_navn: d.kunde.navn, kunde_fornavn: fornavn(d.kunde.navn), kunde_email: d.kunde.email, kunde_telefon: d.kunde.telefon.replace('+45', ''), telefon: d.telefon, firma: d.firma };
      emne = kanal === 'mail' ? udfyld(rens(a.emne ?? '', 200), v) : '';
      tekst = udfyld(raa, v);
    }
    const kundelogin = kanal === 'mail' && tekst.includes(LINK_PLADSHOLDER);
    const vis = medPoweredBy(tekst, kanal, d.afsender.powered);
    ud.push({ kanal, til, fra: kanal === 'mail' ? d.afsender.mail : d.afsender.sms, emne, tekst, vis, skabelon, kundelogin, link, ...(ics ? { ics } : {}), dele: kanal === 'sms' ? smsDele(vis) : 0 });
  }
  return ud;
}

const fornavn = (navn: string) => navn.trim().split(/\s+/)[0] ?? '';
